import { Footer } from '../components/Footer.js';
import { Nav } from '../components/Nav.js';
import { TabletDevice } from '../components/TabletDevice.js';

const STEPS = [
  { n: '01', title: 'Commandez la tablette', desc: 'Elle arrive préconfigurée en 5 à 7 jours.' },
  {
    n: '02',
    title: 'Créez le foyer',
    desc: 'Prénom de votre proche, fuseau horaire, taille des caractères.',
  },
  {
    n: '03',
    title: 'Invitez la famille',
    desc: 'Un lien, une minute, et ils peuvent envoyer leur premier message.',
  },
] as const;

const POUR_QUI = [
  {
    role: 'senior',
    tag: 'Pour votre proche',
    title: 'Rien à apprendre, rien à faire.',
    items: [
      { t: 'Heure et agenda en très grand', d: 'Constamment visible, sans action.' },
      { t: "Alertes qui restent à l'écran", d: "Jusqu'à ce qu'il touche le gros bouton." },
      { t: 'Photos de famille en diaporama', d: "Apparaissent quand l'écran est au repos." },
    ],
  },
  {
    role: 'proche',
    tag: 'Pour vous',
    title: "Cinq minutes par jour, de n'importe où.",
    items: [
      { t: 'App mobile intuitive', d: 'iOS, Android, navigateur desktop.' },
      { t: 'Messages et photos en temps réel', d: 'Votre proche voit en quelques secondes.' },
      { t: 'Agenda partagé entre proches', d: "Plus de doublon, plus d'oubli." },
    ],
  },
] as const;

const CONFIANCE = [
  {
    icon: '🔒',
    title: 'Données souveraines',
    desc: 'Tout reste chiffré sur la tablette à votre domicile. Pas de cloud, pas de tiers.',
  },
  {
    icon: '🛡',
    title: 'RGPD natif',
    desc: "Export ZIP ou suppression définitive en un clic depuis l'app.",
  },
  {
    icon: '⚡',
    title: 'Robuste 24/7',
    desc: 'Watchdog systemd, backup chiffré quotidien, reprise automatique sur coupure.',
  },
  {
    icon: '∞',
    title: 'Sans engagement',
    desc: "Aucun abonnement obligatoire. Résiliation immédiate si vous changez d'avis.",
  },
] as const;

const TEMOIGNAGES = [
  {
    quote:
      "Mamie nous appelle moins pour nous dire qu'elle a oublié son rendez-vous. C'est la paix pour tout le monde.",
    name: 'Julie M.',
    role: "Fille d'une utilisatrice",
  },
  {
    quote:
      "Mon père ne touchait plus à sa tablette depuis des mois. Là il regarde l'écran Cadran plusieurs fois par jour.",
    name: 'Thomas K.',
    role: "Fils d'un utilisateur",
  },
  {
    quote:
      "L'installation m'a pris vingt minutes, chrono en main. Et mon père était ravi de voir la photo de son petit-fils.",
    name: 'Isabelle R.',
    role: "Fille d'un utilisateur",
  },
] as const;

const FAQ_SHORT = [
  {
    q: 'Faut-il être technique pour installer ?',
    a: 'Non. La tablette est préconfigurée. Si vous savez connecter un appareil au Wi-Fi, vous savez installer Cadran.',
  },
  {
    q: 'Et si Internet tombe à la maison ?',
    a: "L'heure, l'agenda et les alertes déjà programmées continuent d'afficher. Les nouveaux messages attendent le retour réseau.",
  },
  {
    q: 'Combien de proches peuvent rejoindre ?',
    a: "Autant que vous voulez. L'optimal est 1 à 3 par foyer pour éviter les doublons.",
  },
] as const;

export function Accueil() {
  return (
    <>
      <Nav active="home" />
      <main>
        {/* ——— Hero centré ——— */}
        <section className="hero hero-centered">
          <div className="container">
            <div className="hero-center-text">
              <div className="eyebrow">Cadran · pour les familles éloignées</div>
              <h1
                className="display"
                style={{ fontSize: 'clamp(60px, 8.4vw, 132px)', margin: '14px 0 0' }}
              >
                L'horloge qui montre
                <br />
                aussi les{' '}
                <em>
                  gens
                  <br />
                  qu'on aime
                </em>
                .
              </h1>
              <p className="hero-lede">
                Un cadre posé sur le buffet de Mamie. Il indique l'heure, la journée, et les
                rendez-vous. Tu lui envoies un mot, une photo — ça apparaît, simplement.
              </p>
              <div className="hero-ctas">
                <a className="btn btn-primary btn-lg btn-arrow" href="/inscription">
                  Créer un foyer
                </a>
                <a className="btn btn-secondary btn-lg" href="/comment-ca-marche">
                  Comment ça marche
                </a>
              </div>
              <p className="hero-note">
                Tablette livrée prête à brancher. À partir de <b>249 €</b> · sans engagement.
              </p>
            </div>
            <div className="hero-center-device">
              <TabletDevice />
            </div>
          </div>
        </section>

        {/* ——— Problème ——— */}
        <section className="section section-probleme">
          <div className="container">
            <div className="probleme-grid">
              <div>
                <div className="eyebrow" style={{ marginBottom: '16px' }}>
                  Le problème
                </div>
                <p className="probleme-quote serif">
                  "Nos parents oublient leurs rendez-vous. On ne peut pas être là tout le temps. Et
                  les solutions qu'on essaie sont trop compliquées pour eux."
                </p>
              </div>
              <div className="probleme-list">
                <p>
                  Les tablettes grand public sont <em>abandonnées au bout de deux semaines</em> —
                  interface trop complexe, mots de passe oubliés, peur d'effacer quelque chose.
                </p>
                <p>
                  Les visiophones coûtent <em>30 à 50 €/mois</em> et obligent votre proche à
                  décrocher au bon moment.
                </p>
                <p>
                  Cadran inverse la logique : <em>c'est l'écran qui vient à votre proche</em>, pas
                  l'inverse.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ——— Comment ça marche ——— */}
        <section className="section alt section-comment">
          <div className="container">
            <div className="section-head-compact">
              <div className="eyebrow">Comment ça marche</div>
              <h2 className="serif">
                Prêt en <em>vingt minutes</em>.
              </h2>
            </div>
            <div className="steps">
              {STEPS.map((s) => (
                <div key={s.n} className="step">
                  <div className="step-n">{s.n}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
            <div className="steps-cta">
              <a href="/comment-ca-marche" className="btn btn-secondary btn-lg">
                Voir le détail →
              </a>
            </div>
          </div>
        </section>

        {/* ——— Pour qui ——— */}
        <section className="section">
          <div className="container">
            <div className="section-head">
              <div className="meta">
                <div className="eyebrow">Pour qui</div>
              </div>
              <h2 className="serif">
                Deux vies, <em>un seul écran</em>.
              </h2>
            </div>
            <div className="pourqui-grid">
              {POUR_QUI.map((card) => (
                <div
                  key={card.role}
                  className={`pourqui-card${card.role === 'senior' ? ' senior' : ''}`}
                >
                  <div className="pourqui-tag">{card.tag}</div>
                  <h3 className="serif">{card.title}</h3>
                  <ul className="pourqui-list">
                    {card.items.map((item) => (
                      <li key={item.t}>
                        <b>{item.t}</b>
                        {item.d}
                      </li>
                    ))}
                  </ul>
                  <div className="pourqui-photo">
                    <div
                      className={`placeholder-img mini-photo${card.role === 'proche' ? ' warm' : ''}`}
                    >
                      <div className="ph-caption">
                        {card.role === 'senior'
                          ? "photo · l'écran Cadran chez Mamie"
                          : "photo · l'app depuis votre téléphone"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ——— Confiance ——— */}
        <section className="section ink">
          <div className="container">
            <div className="section-head">
              <div className="meta">
                <div className="eyebrow">Confiance</div>
              </div>
              <h2>
                Vos données, <em>chez vous</em>.
              </h2>
            </div>
            <div className="confiance-grid">
              {CONFIANCE.map((item) => (
                <div key={item.title} className="conf-item">
                  <div className="conf-icon" aria-hidden="true">
                    {item.icon}
                  </div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ——— Tarifs preview ——— */}
        <section className="section">
          <div className="container">
            <div className="section-head-compact">
              <div className="eyebrow">Tarifs</div>
              <h2 className="serif">
                Simple comme <em>bonjour</em>.
              </h2>
            </div>
            <div className="tarifs-grid">
              {[
                {
                  id: 'tablette',
                  name: 'Tablette seule',
                  price: '249',
                  monthly: null as string | null,
                  tag: "L'essentiel.",
                  featured: false,
                  cta: 'Commander',
                },
                {
                  id: 'famille',
                  name: 'Famille',
                  price: '249',
                  monthly: '10',
                  tag: 'Le plus populaire.',
                  featured: true,
                  badge: 'Populaire',
                  cta: 'Choisir Famille',
                },
                {
                  id: 'presence',
                  name: 'Présence plus',
                  price: '249',
                  monthly: '30',
                  tag: '4G intégrée.',
                  featured: false,
                  cta: 'Choisir Présence plus',
                },
              ].map((p) => (
                <div key={p.id} className={`tarif-card${p.featured ? ' featured' : ''}`}>
                  {'badge' in p && p.badge && <div className="tarif-badge">{p.badge}</div>}
                  <div className="serif tarif-name">{p.name}</div>
                  <div className="tarif-price">
                    <b>{p.price} €</b>
                    {p.monthly ? ` + ${p.monthly} €/mois` : ' · une fois'}
                  </div>
                  <p className="tarif-tag">{p.tag}</p>
                  <ul style={{ flex: 1 }} />
                  <a
                    href="/inscription"
                    className={`btn ${p.featured ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    {p.cta}
                  </a>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <a href="/tarifs" className="small" style={{ color: 'var(--terracotta-deep)' }}>
                Voir le comparatif détaillé →
              </a>
            </div>
          </div>
        </section>

        {/* ——— Témoignages ——— */}
        <section className="section alt">
          <div className="container">
            <div className="section-head-compact">
              <div className="eyebrow">Témoignages</div>
              <h2 className="serif">
                Ce qu'ils <em>en disent</em>.
              </h2>
            </div>
            <div className="temoignages-grid">
              {TEMOIGNAGES.map((t) => (
                <figure key={t.name} className="temoignage">
                  <blockquote>« {t.quote} »</blockquote>
                  <figcaption>
                    <div className="t-photo placeholder-img" />
                    <div>
                      <div className="t-name">{t.name}</div>
                      <div className="t-role">{t.role}</div>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ——— FAQ courte ——— */}
        <section className="section">
          <div className="container">
            <div className="section-head">
              <div className="meta">
                <div className="eyebrow">Questions fréquentes</div>
              </div>
              <h2 className="serif">
                Avant de <em>se lancer</em>.
              </h2>
            </div>
            <div className="faq-list">
              {FAQ_SHORT.map((item) => (
                <details key={item.q} className="faq-item">
                  <summary>
                    {item.q}
                    <span className="faq-plus" aria-hidden="true">
                      +
                    </span>
                  </summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
            <div className="faq-cta">
              <a href="/faq" className="btn btn-secondary">
                Toutes les questions →
              </a>
            </div>
          </div>
        </section>

        {/* ——— CTA final ——— */}
        <section className="section-cta ink">
          <div className="container" style={{ display: 'grid', placeItems: 'center' }}>
            <div className="cta-final display">
              <h2>
                L'horloge qui dit <em>tu comptes</em>.
              </h2>
              <p>Livraison en 5 à 7 jours. Prête à brancher.</p>
              <div className="cta-row">
                <a href="/inscription" className="btn btn-accent btn-lg btn-arrow">
                  Créer un foyer
                </a>
                <a
                  href="/comment-ca-marche"
                  className="btn btn-secondary btn-lg"
                  style={{ borderColor: 'oklch(0.40 0.02 60)', color: 'oklch(0.92 0.014 78)' }}
                >
                  Comment ça marche
                </a>
              </div>
              <p className="cta-note small">
                249 € · sans abonnement obligatoire · 30 j satisfait ou remboursé
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
